import React, { useEffect, useState } from 'react';
import * as z from 'zod';
import {
  ArrowRight, Backpack, BadgeCheck, LucideSkipBack, LucideStepBack, SendToBackIcon,
  SkipBack,
  StepBack,
  StepForward,
  Undo2
} from 'lucide-react';

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { format, set } from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import { toast } from '@/hooks/use-toast';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '@/lib/utils';
import { useUserInfo } from "@/hooks/useUserInfo.ts";
import { getSummary, sendRemoteWorkRequest } from "@/utils/pasUtils.js";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import 'react-time-picker/dist/TimePicker.css'; // Import default styles
import 'react-clock/dist/Clock.css'; // Required for react-time-picker

const formSchema = z.object({
  employee: z.string({
    required_error: 'الموظف is required.'
  }),
  from_date: z.string({
    required_error: 'من يوم is required.'
  }),
  to_date: z.string({
    required_error: 'إلى يوم is required.'
  }),
  reason: z.string({
    required_error: ' السبب is required.'
  }).min(1, " السبب مطلوب"),
  start_time: z.string({
    required_error: 'وقت البدء is required.'
  }),
  end_time: z.string({
    required_error: 'وقت الانتهاء is required.'
  }),
  total_hours: z.string({
    required_error: 'إجمالي الساعات is required.'
  }),
  tasks: z.array(z.object({
    task: z.string().optional(),
    time_hr: z.string().optional(),
  })).optional(),
  total_days: z.string().optional(),
});


const ServiceRemoteWork = () => {
  const VITE_USERNAMET = import.meta.env.VITE_USERNAMET
  const VITE_PASSWORD = import.meta.env.VITE_PASSWORD

  const { t } = useLanguage();
  const navigate = useNavigate();
  const [isSaved, setIsSaved] = useState(false);
  const [showPrintView, setShowPrintView] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState({
    profile: null,
    loading: true,
    error: null

  });
  const { greeting, fullName, email, role, profilePictureUrl } = useUserInfo();
  const [selectedFromDate, setSelectedFromDate] = useState<Date | null>(null);
  const [selectedToDate, setSelectedToDate] = useState<Date | null>(null);
  const [selectedStartTime, setSelectedStartTime] = useState('10:00');
  const [selectedEndTime, setSelectedEndTime] = useState('11:00');
  const [totalHours, setTotalHours] = useState('');
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      employee: data.profile ? `${data.profile.name}` : '',
      from_date: '',
      to_date: '',
      reason: '-',
      start_time: new Date().toISOString().split('T')[0],
      end_time: new Date().toISOString().split('T')[0],
      total_hours: '',
      tasks: [{ task: '', time_hr: '' }],
      total_days: "0",
    },
  });
  useEffect(() => {
    async function fetchAttendance() {
      try {
        setLoading(true);

        if (email) {
          // Fetch summary data based on email    
          const summaryResponse = await getSummary(
            VITE_USERNAMET,
            VITE_PASSWORD,
            email
          );
          console.log("Profile data:", summaryResponse.profile);
          if (data.loading) {
            setData((prev) => ({
              ...prev,
              profile: summaryResponse.profile,
              loading: false,
              error: null,
            }));
            console.log("Profile data:", data);
            form.reset({
              employee: `${summaryResponse.profile.name}`,
              from_date: '',
              to_date: '',
              reason: '-',
              start_time: '08:00', // Set actual start time
              end_time: '08:00',   // Set actual end time
              total_hours: '00', // Set actual total hours
              tasks: [{ task: '', time_hr: '' }],
              total_days: "0",
            });
          }
        }
      } catch (err) {
        setError(err.message || "Failed to fetch attendance");
        console.log(err.message || "Failed to fetch attendance");
      } finally {
        setLoading(false);
        console.log("Profile data:", data.profile);
      }
    }

    fetchAttendance();
  }, [data, email, form, totalHours]);
  const calculateTotalHours = (): string => {
    const start = form.getValues('start_time');
    const end = form.getValues('end_time');
    if (!start || !end) return '';

    const [startHours, startMinutes] = start.split(':').map(Number);
    const [endHours, endMinutes] = end.split(':').map(Number);

    const startTotalMinutes = startHours * 60 + startMinutes;
    const endTotalMinutes = endHours * 60 + endMinutes;

    const totalMinutes = endTotalMinutes - startTotalMinutes;

    if (totalMinutes <= 0) return '';

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const decimalHours = totalMinutes / 60;

    form.reset({
      employee: form.getValues('employee'),
      from_date: form.getValues('from_date'),
      to_date: form.getValues('to_date'),
      reason: form.getValues('reason'),
      start_time: form.getValues('start_time'),
      end_time: form.getValues('end_time'),
      total_hours: `${decimalHours.toFixed(2)}`,
      tasks: form.getValues('tasks'),
      total_days: form.getValues('total_days'),
    });
    setTotalHours(`${decimalHours.toFixed(2)}`);
  };

  const calculateTotalDays = (): string => {
    const fromDate = form.getValues('from_date');
    const toDate = form.getValues('to_date');
    if (!fromDate || !toDate) return '0';

    const from = new Date(fromDate);
    const to = new Date(toDate);

    const timeDiff = to.getTime() - from.getTime();
    const dayDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1; // +1 to include both start and end dates

    form.reset({
      employee: form.getValues('employee'),
      from_date: form.getValues('from_date'),
      to_date: form.getValues('to_date'),
      reason: form.getValues('reason'),
      start_time: form.getValues('start_time'),
      end_time: form.getValues('end_time'),
      total_hours: form.getValues('total_hours'),
      tasks: form.getValues('tasks'),
      total_days: dayDiff > 0 ? dayDiff.toString() : '0',
    });
    return dayDiff > 0 ? dayDiff.toString() : '0';
  };



  async function onSubmit(values: z.infer<typeof formSchema>) {
    console.log(values);
    setIsSaved(true);
    toast({
      title: 'تم إرسال الطلب بنجاح',
      description: 'سيتم معالجة الطلب من قبل المسؤولين في النظام حسب التسلسل.',
    });
    try {
      const response = await sendRemoteWorkRequest(VITE_USERNAMET,
        VITE_PASSWORD,
        {
          employee: form.getValues('employee'),
          from_date: form.getValues('from_date'),
          to_date: form.getValues('to_date'),
          reason: form.getValues('reason'),
          start_time: form.getValues('start_time'),
          end_time: form.getValues('end_time'),
          total_hours: form.getValues('total_hours'),
          tasks: form.getValues('tasks'),
          total_days: form.getValues('total_days'),
        });
      toast({
        title: 'تم إرسال الطلب  بنجاح',
        description: 'سيتم مراجعة طلبك من قبل الإدارة.',
        duration: 5000,
      });
      navigate('/services');
    } catch (error) {
      console.error("Request failed:", error);
      toast({
        title: 'فشل في إرسال الطلب ',
        description: error.message || 'حدث خطأ أثناء معالجة طلبك. يرجى المحاولة مرة أخرى.',
        duration: 5000,
        variant: 'destructive',
      });
    }
  }


  return (
    <>
      {data.loading ? (
        <div>Loading...</div>
      ) : data.profile ? (
        <div className="w-full max-w-4xl">
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate('/services')}
              className="gap-2"
            >
              <ArrowRight className="h-4 w-4" />
              العودة للخدمات
            </Button>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-2xl font-bold text-right">{t('services-remote-work.title')}</CardTitle>
              <CardDescription className="text-right">
                يرجى ملء جميع البيانات المطلوبة لتقديم {t('services-remote-work.title')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">


                    {data.profile.name && <FormField
                      control={form.control}
                      name="employee"
                      rules={{
                        required: "الموظف is required." // Custom error message
                      }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-right block">الموظف</FormLabel>
                          <FormControl>
                            <Input
                              readOnly={true}
                              placeholder="الموظف..."
                              className="text-right"
                              {...field}
                            />
                          </FormControl>

                          <FormMessage />
                        </FormItem>
                      )}
                    />}
                    <FormField
                      control={form.control}
                      name="from_date"
                      rules={{
                        required: "من يوم مطلوب"
                      }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-right block">من يوم</FormLabel>
                          <FormControl>
                            <DatePicker
                              selected={selectedFromDate}
                              onChange={(date: Date | null) => {
                                setSelectedFromDate(date);
                                //field.onChange(date ? date.toISOString().split('T')[0] : '');
                                const dateString = date.toLocaleDateString('en-CA'); // 'en-CA' gives YYYY-MM-DD
                                field.onChange(dateString);
                                calculateTotalDays();
                              }}
                              dateFormat="yyyy/MM/dd"
                              placeholderText="اختر من يوم..."
                              className="w-full p-2 border border-gray-300 rounded-md text-right"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="to_date"
                      rules={{
                        required: "إلى يوم مطلوب"
                      }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-right block">إلى يوم</FormLabel>
                          <FormControl>
                            <DatePicker
                              selected={selectedToDate}
                              onChange={(date: Date | null) => {
                                setSelectedToDate(date);
                                // field.onChange(date ? date.toISOString().split('T')[0] : '');
                                const dateString = date.toLocaleDateString('en-CA'); // 'en-CA' gives YYYY-MM-DD
                                field.onChange(dateString);
                                calculateTotalDays();
                              }}
                              dateFormat="yyyy/MM/dd"
                              placeholderText="اختر إلى يوم..."
                              className="w-full p-2 border border-gray-300 rounded-md text-right"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="total_days"
                      rules={{
                        required: "إجمالي الأيام مطلوب",
                      }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-right block">إجمالي الأيام</FormLabel>
                          <FormControl>
                            <Input
                              readOnly={true}
                              placeholder="إجمالي عدد الأيام..."
                              className="text-right"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="reason"
                      rules={{
                        required: "السبب is required." // Custom error message
                      }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-right block">السبب</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="السبب..."
                              className="text-right"
                              {...field}
                            />
                          </FormControl>

                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField

                      control={form.control}
                      name="start_time"
                      rules={{
                        required: "وقت البدء is required." // Custom error message
                      }}
                      render={({ field }) => (
                        <FormItem dir='rtl'>
                          <FormLabel className="text-right block">وقت البدء</FormLabel>
                          <FormControl>

                            <Input
                              type="time"
                              className="text-right text-lg flex justify-between flex-row-reverse"
                              {...field}
                              step="300"
                              onChange={(e) => {
                                field.onChange(e); // Update react-hook-form state
                                setSelectedStartTime(e.target.value); // Update local state
                                calculateTotalHours(); // Recalculate total hours
                              }}
                            />
                          </FormControl>

                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="end_time"
                      rules={{
                        required: "وقت الانتهاء is required." // Custom error message
                      }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-right block">وقت الانتهاء</FormLabel>
                          <FormControl>
                            <Input
                              type="time"
                              className="text-right text-lg flex justify-between flex-row-reverse"
                              {...field}
                              onChange={(e) => {
                                field.onChange(e); // Update react-hook-form state
                                setSelectedEndTime(e.target.value); // Update local state
                                calculateTotalHours(); // Recalculate total hours
                              }}
                              step="300"
                            />
                          </FormControl>

                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="total_hours"
                      rules={{
                        required: "إجمالي الساعات is required." // Custom error message
                      }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-right block">إجمالي الساعات</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="إجمالي الساعات..."
                              className="text-right"
                              {...field}
                            />
                          </FormControl>

                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="tasks"
                      rules={{
                        required: "المهام مطلوبة"
                      }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-right block">المهام</FormLabel>
                          <FormControl>
                            <div className="space-y-3">
                              {field.value?.map((task, index) => (
                                <div key={index} className="flex gap-2 items-start">
                                  <Input
                                    placeholder="وصف المهمة..."
                                    className="text-right flex-1"
                                    value={task.task || ''}
                                    onChange={(e) => {
                                      const newTasks = [...field.value];
                                      newTasks[index] = { ...newTasks[index], task: e.target.value };
                                      field.onChange(newTasks);
                                    }}
                                  />
                                  <Input
                                    type="time"
                                    className="text-right w-32"
                                    value={task.time_hr || ''}
                                    onChange={(e) => {
                                      const newTasks = [...field.value];
                                      newTasks[index] = { ...newTasks[index], time_hr: e.target.value };
                                      field.onChange(newTasks);
                                    }}
                                  />
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      const newTasks = field.value.filter((_, i) => i !== index);
                                      field.onChange(newTasks);
                                    }}
                                    className="text-red-500 hover:text-red-700"
                                  >
                                    ✕
                                  </Button>
                                </div>
                              ))}

                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                  const newTasks = [...(field.value || []), { task: '', time_hr: '' }];
                                  field.onChange(newTasks);
                                }}
                                className="w-full"
                              >
                                + إضافة مهمة
                              </Button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                  </div>


                  <div className="flex gap-4 justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate('/services')}
                    >
                      إلغاء
                    </Button>
                    {/* <Button type="submit" disabled={isSaved}>
                  حفظ
                </Button> */}
                    <Button type="submit"
                    // onClick={handlePrint}
                    >
                      إرسال الطلب
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div >
      ) : (
        <div>No profile data available</div>
      )}
    </>
  );
};

export default ServiceRemoteWork;